import { useState, useEffect, useRef } from 'react'
import { migrationsAPI } from '../../services/api'

import { logger } from '@/utils/logger'

interface PendingRevision {
  revision: string
  doc: string
}

interface Snapshot {
  database: string
  mode?: string
  current?: string[]
  heads?: string[]
  pending?: PendingRevision[]
  history?: PendingRevision[]
  is_behind?: boolean
  error?: string
}

interface RunSummary {
  id: number
  app: string
  action: string
  target: string | null
  status: string
  exit_code: number | null
  backup_status: string
  output_truncated: boolean
  started_at: string
  finished_at: string | null
}

interface RunDetail extends RunSummary {
  output: string
  cancel_requested: boolean
  backup_path: string | null
  before_state: Snapshot | null
  after_state: Snapshot | null
}

interface AppMigrationStatus {
  app: string
  display_name: string
  kind: 'sql_runner' | 'alembic'
  snapshot: Snapshot | null
  last_run: RunSummary | null
}

const RUN_TERMINAL_STATUSES = ['success', 'partial', 'failed', 'timeout', 'cancelled', 'unknown']

export default function MigrationsManagement() {
  const [consoleEnabled, setConsoleEnabled] = useState(false)
  const [consoleReason, setConsoleReason] = useState<string | null>(null)
  const [apps, setApps] = useState<AppMigrationStatus[]>([])
  const [loading, setLoading] = useState(true)

  const [upgradeConfirmApp, setUpgradeConfirmApp] = useState<string | null>(null)
  const [upgradeConfirmInput, setUpgradeConfirmInput] = useState('')

  const [downgradeConfirmApp, setDowngradeConfirmApp] = useState<string | null>(null)
  const [downgradeConfirmInput, setDowngradeConfirmInput] = useState('')
  const [downgradeTarget, setDowngradeTarget] = useState('')

  const [stampConfirmApp, setStampConfirmApp] = useState<string | null>(null)
  const [stampConfirmInput, setStampConfirmInput] = useState('')
  const [stampTarget, setStampTarget] = useState('')

  const [startingApp, setStartingApp] = useState<string | null>(null)
  const [viewingRun, setViewingRun] = useState<RunDetail | null>(null)
  const [autoScroll, setAutoScroll] = useState(true)
  const pollIntervalRef = useRef<number | null>(null)
  const outputRef = useRef<HTMLPreElement | null>(null)

  useEffect(() => {
    loadApps()
    const interval = setInterval(() => loadApps(), 30000)
    return () => {
      clearInterval(interval)
      stopPolling()
    }
  }, [])

  useEffect(() => {
    if (autoScroll && outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight
    }
  }, [viewingRun?.output])

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      window.clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
  }

  const pollRun = async (runId: number) => {
    try {
      const response = await migrationsAPI.getRun(runId)
      setViewingRun(response.data)
      if (RUN_TERMINAL_STATUSES.includes(response.data.status)) {
        stopPolling()
        loadApps()
      }
    } catch (error) {
      logger.error('Error polling migration run:', error)
    }
  }

  const startPolling = (runId: number) => {
    stopPolling()
    pollIntervalRef.current = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return
      pollRun(runId)
    }, 1000)
  }

  const loadApps = async () => {
    try {
      const response = await migrationsAPI.getApps()
      setConsoleEnabled(response.data.console_enabled)
      setConsoleReason(response.data.console_reason)
      setApps(response.data.apps)
    } catch (error) {
      logger.error('Error loading migrations:', error)
    } finally {
      setLoading(false)
    }
  }

  const openRunModal = (runId: number, app: string, action: string) => {
    setAutoScroll(true)
    setViewingRun({
      id: runId, app, action, target: null, status: 'running', exit_code: null,
      backup_status: 'pending', output_truncated: false, started_at: new Date().toISOString(),
      finished_at: null, output: '', cancel_requested: false, backup_path: null,
      before_state: null, after_state: null,
    })
    startPolling(runId)
    pollRun(runId)
  }

  const handleCloseRunModal = () => {
    setViewingRun(null)
    stopPolling()
  }

  const handleOutputScroll = () => {
    const el = outputRef.current
    if (!el) return
    setAutoScroll(el.scrollHeight - el.scrollTop - el.clientHeight < 40)
  }

  const handleConfirmUpgrade = async () => {
    if (!upgradeConfirmApp) return
    const app = upgradeConfirmApp
    setUpgradeConfirmApp(null)
    setUpgradeConfirmInput('')
    setStartingApp(app)
    try {
      const response = await migrationsAPI.upgrade(app)
      openRunModal(response.data.run_id, app, 'upgrade')
    } catch (error: any) {
      logger.error('Error starting upgrade:', error)
      alert(error?.response?.data?.detail || 'Fehler beim Starten des Upgrades')
    } finally {
      setStartingApp(null)
      loadApps()
    }
  }

  const handleConfirmDowngrade = async () => {
    if (!downgradeConfirmApp || !downgradeTarget) return
    const app = downgradeConfirmApp
    setDowngradeConfirmApp(null)
    setDowngradeConfirmInput('')
    setStartingApp(app)
    try {
      const response = await migrationsAPI.downgrade(app, downgradeTarget)
      openRunModal(response.data.run_id, app, 'downgrade')
    } catch (error: any) {
      logger.error('Error starting downgrade:', error)
      alert(error?.response?.data?.detail || 'Fehler beim Starten des Downgrades')
    } finally {
      setStartingApp(null)
      setDowngradeTarget('')
      loadApps()
    }
  }

  const handleConfirmStamp = async () => {
    if (!stampConfirmApp || !stampTarget) return
    const app = stampConfirmApp
    setStampConfirmApp(null)
    setStampConfirmInput('')
    setStartingApp(app)
    try {
      const response = await migrationsAPI.stamp(app, stampTarget)
      openRunModal(response.data.run_id, app, 'stamp')
    } catch (error: any) {
      logger.error('Error starting stamp:', error)
      alert(error?.response?.data?.detail || 'Fehler beim Setzen des Stamps')
    } finally {
      setStartingApp(null)
      setStampTarget('')
      loadApps()
    }
  }

  const handleCancelRun = async () => {
    if (!viewingRun) return
    try {
      await migrationsAPI.cancel(viewingRun.id)
    } catch (error) {
      logger.error('Error cancelling migration:', error)
    }
  }

  const getStatusTag = (status?: string | null) => {
    switch (status) {
      case 'running':
        return { className: 'wc-tag-accent', label: 'Läuft' }
      case 'success':
        return { className: 'wc-tag-accent', label: 'Erfolgreich' }
      case 'partial':
        return { className: 'wc-tag-outline', style: { borderColor: '#fbbf24', color: '#fbbf24' }, label: 'Teilerfolg' }
      case 'failed':
        return { className: 'wc-tag-outline', style: { borderColor: '#fca5a5', color: '#fca5a5' }, label: 'Fehlgeschlagen' }
      case 'timeout':
        return { className: 'wc-tag-outline', style: { borderColor: '#fca5a5', color: '#fca5a5' }, label: 'Zeitüberschreitung' }
      case 'cancelled':
        return { className: 'wc-tag-neutral', label: 'Abgebrochen' }
      case 'unknown':
        return { className: 'wc-tag-neutral', label: 'Unbekannt' }
      default:
        return { className: 'wc-tag-neutral', label: 'Nie gelaufen' }
    }
  }

  const getAppStatusTag = (app: AppMigrationStatus) => {
    if (app.snapshot?.error) return { className: 'wc-tag-outline', style: { borderColor: '#fca5a5', color: '#fca5a5' }, label: 'Fehler' }
    if (app.snapshot?.is_behind) return { className: 'wc-tag-outline', style: { borderColor: '#fbbf24', color: '#fbbf24' }, label: 'Im Rückstand' }
    return { className: 'wc-tag-accent', label: 'Aktuell' }
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
        <div className="wc-spinner" />
      </div>
    )
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, margin: 0 }}>Migrationen</h1>
          <p className="wc-muted" style={{ fontSize: 14, margin: '4px 0 0' }}>
            Datenbank-Migrationen ohne SSH-Zugang ausführen — jede Migration erstellt vorher automatisch eine Sicherung
          </p>
        </div>
        <button onClick={() => loadApps()} className="wc-btn wc-btn-secondary">Aktualisieren</button>
      </div>

      {!consoleEnabled && (
        <div className="wc-alert wc-alert-info" style={{ marginBottom: 16 }}>
          Admin-Konsole ist deaktiviert{consoleReason ? `: ${consoleReason}` : ''}.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 20 }}>
        {apps.map((a) => {
          const tag = getAppStatusTag(a)
          const isRunning = a.last_run?.status === 'running'
          const pending = a.snapshot?.pending || []
          return (
            <div key={a.app} className="wc-card wc-elev-sm" style={{ gap: 14 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                <div>
                  <h3 className="wc-card-title" style={{ margin: 0 }}>{a.display_name}</h3>
                  <p className="wc-muted wc-mono" style={{ fontSize: 12, margin: '2px 0 0' }}>
                    {a.snapshot?.database || a.app} · {a.kind === 'alembic' ? 'Alembic' : 'SQL-Runner'}
                  </p>
                </div>
                <span className={`wc-tag ${tag.className}`} style={tag.style}>{tag.label}</span>
              </div>

              {a.app === 'portal' && (
                <div className="wc-alert wc-alert-info" style={{ fontSize: 12, padding: '8px 12px' }}>
                  Portal migriert seine eigene Datenbank — die Konsole kann währenddessen kurz hängen.
                </div>
              )}

              {pending.length > 0 && (
                <div style={{ fontSize: 13, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span className="wc-muted">{pending.length} Migration(en) ausstehend:</span>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {pending.map((p) => (
                      <li key={p.revision} className="wc-mono" style={{ fontSize: 12 }}>
                        {p.revision} — <span style={{ fontFamily: 'inherit' }}>{p.doc}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {a.last_run && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span className="wc-muted">Letzter Lauf:</span>
                  <span>{getStatusTag(a.last_run.status).label} · {new Date(a.last_run.started_at).toLocaleString('de-DE')}</span>
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, paddingTop: 12, borderTop: '1px solid var(--wc-divider)', flexWrap: 'wrap' }}>
                <button
                  onClick={() => { setUpgradeConfirmApp(a.app); setUpgradeConfirmInput('') }}
                  disabled={!consoleEnabled || startingApp === a.app || isRunning}
                  className="wc-btn wc-btn-primary"
                  style={{ flex: 1 }}
                >
                  {isRunning ? 'Läuft...' : 'Upgrade'}
                </button>
                {a.kind === 'alembic' && (
                  <>
                    <button
                      onClick={() => { setDowngradeConfirmApp(a.app); setDowngradeConfirmInput(''); setDowngradeTarget('') }}
                      disabled={!consoleEnabled || startingApp === a.app || isRunning}
                      className="wc-btn wc-btn-secondary"
                    >
                      Downgrade
                    </button>
                    <button
                      onClick={() => { setStampConfirmApp(a.app); setStampConfirmInput(''); setStampTarget('') }}
                      disabled={!consoleEnabled || startingApp === a.app || isRunning}
                      className="wc-btn wc-btn-ghost"
                    >
                      Stamp
                    </button>
                  </>
                )}
                {a.last_run && (
                  <button onClick={() => openRunModal(a.last_run!.id, a.app, a.last_run!.action)} className="wc-btn wc-btn-ghost">
                    Letzter Lauf
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Upgrade Confirmation */}
      {upgradeConfirmApp && (
        <div className="wc-modal-overlay" onClick={() => setUpgradeConfirmApp(null)}>
          <div className="wc-modal wc-modal-sm" onClick={(e) => e.stopPropagation()}>
            <h3 className="wc-card-title" style={{ margin: 0 }}>Upgrade starten?</h3>
            <p className="wc-card-body" style={{ margin: 0 }}>
              Führt alle offenen Migrationen für <strong>{apps.find(a => a.app === upgradeConfirmApp)?.display_name}</strong> aus.
              Vorher wird automatisch eine Sicherung erstellt. Zum Bestätigen App-Namen eingeben: <code className="wc-mono">{upgradeConfirmApp}</code>
            </p>
            <input
              type="text"
              value={upgradeConfirmInput}
              onChange={(e) => setUpgradeConfirmInput(e.target.value)}
              placeholder={upgradeConfirmApp}
              className="wc-input"
              autoFocus
            />
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => setUpgradeConfirmApp(null)} className="wc-btn wc-btn-secondary" style={{ flex: 1 }}>Abbrechen</button>
              <button
                onClick={handleConfirmUpgrade}
                disabled={upgradeConfirmInput !== upgradeConfirmApp}
                className="wc-btn wc-btn-primary"
                style={{ flex: 1 }}
              >
                Upgrade starten
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Downgrade Confirmation */}
      {downgradeConfirmApp && (
        <div className="wc-modal-overlay" onClick={() => setDowngradeConfirmApp(null)}>
          <div className="wc-modal wc-modal-sm" onClick={(e) => e.stopPropagation()}>
            <h3 className="wc-card-title" style={{ margin: 0 }}>Downgrade starten?</h3>
            <p className="wc-card-body" style={{ margin: 0 }}>
              Setzt <strong>{apps.find(a => a.app === downgradeConfirmApp)?.display_name}</strong> auf eine frühere Revision zurück.
              Vorher wird automatisch eine Sicherung erstellt.
            </p>
            <select value={downgradeTarget} onChange={(e) => setDowngradeTarget(e.target.value)} className="wc-input">
              <option value="">Ziel-Revision wählen...</option>
              {(apps.find(a => a.app === downgradeConfirmApp)?.snapshot?.history || []).map((h) => (
                <option key={h.revision} value={h.revision}>{h.revision} — {h.doc}</option>
              ))}
            </select>
            <p className="wc-muted" style={{ fontSize: 12, margin: 0 }}>
              Zum Bestätigen App-Namen eingeben: <code className="wc-mono">{downgradeConfirmApp}</code>
            </p>
            <input
              type="text"
              value={downgradeConfirmInput}
              onChange={(e) => setDowngradeConfirmInput(e.target.value)}
              placeholder={downgradeConfirmApp}
              className="wc-input"
            />
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => setDowngradeConfirmApp(null)} className="wc-btn wc-btn-secondary" style={{ flex: 1 }}>Abbrechen</button>
              <button
                onClick={handleConfirmDowngrade}
                disabled={downgradeConfirmInput !== downgradeConfirmApp || !downgradeTarget}
                className="wc-btn wc-btn-primary"
                style={{ flex: 1 }}
              >
                Downgrade starten
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stamp Confirmation */}
      {stampConfirmApp && (
        <div className="wc-modal-overlay" onClick={() => setStampConfirmApp(null)}>
          <div className="wc-modal wc-modal-sm" onClick={(e) => e.stopPropagation()}>
            <h3 className="wc-card-title" style={{ margin: 0 }}>Stamp setzen?</h3>
            <p className="wc-card-body" style={{ margin: 0 }}>
              Markiert <strong>{apps.find(a => a.app === stampConfirmApp)?.display_name}</strong> als auf einer bestimmten Revision stehend,
              <strong> ohne irgendein SQL-Statement auszuführen</strong>. Nur für Wiederherstellung nach einem Fehler gedacht.
              Aktuell: <code className="wc-mono">{apps.find(a => a.app === stampConfirmApp)?.snapshot?.current?.join(', ')}</code>,
              Head: <code className="wc-mono">{apps.find(a => a.app === stampConfirmApp)?.snapshot?.heads?.join(', ')}</code>
            </p>
            <input
              type="text"
              value={stampTarget}
              onChange={(e) => setStampTarget(e.target.value)}
              placeholder="Ziel-Revision, z.B. 021_service_excluded_windows"
              className="wc-input"
            />
            <p className="wc-muted" style={{ fontSize: 12, margin: 0 }}>
              Zum Bestätigen App-Namen eingeben: <code className="wc-mono">{stampConfirmApp}</code>
            </p>
            <input
              type="text"
              value={stampConfirmInput}
              onChange={(e) => setStampConfirmInput(e.target.value)}
              placeholder={stampConfirmApp}
              className="wc-input"
            />
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => setStampConfirmApp(null)} className="wc-btn wc-btn-secondary" style={{ flex: 1 }}>Abbrechen</button>
              <button
                onClick={handleConfirmStamp}
                disabled={stampConfirmInput !== stampConfirmApp || !stampTarget}
                className="wc-btn wc-btn-primary"
                style={{ flex: 1 }}
              >
                Stamp setzen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Run Output Modal */}
      {viewingRun && (
        <div className="wc-modal-overlay" onClick={handleCloseRunModal}>
          <div className="wc-modal wc-modal-lg" style={{ maxHeight: '85vh' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <div>
                <h3 className="wc-card-title" style={{ margin: 0 }}>
                  {apps.find(a => a.app === viewingRun.app)?.display_name || viewingRun.app} · {viewingRun.action}
                  {viewingRun.target ? ` → ${viewingRun.target}` : ''}
                </h3>
                <p className="wc-muted" style={{ fontSize: 13, margin: '4px 0 0' }}>Run #{viewingRun.id}</p>
              </div>
              <button onClick={handleCloseRunModal} className="wc-btn wc-btn-icon wc-btn-ghost" aria-label="Schließen">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div style={{ display: 'flex', gap: 16, fontSize: 13 }}>
              <span className="wc-muted">Sicherung: {
                viewingRun.backup_status === 'success' ? 'erfolgreich' :
                viewingRun.backup_status === 'failed' ? 'fehlgeschlagen' : 'läuft...'
              }</span>
              <span className="wc-muted">Status: {getStatusTag(viewingRun.status).label}</span>
              {viewingRun.exit_code !== null && <span className="wc-muted">Exit-Code: {viewingRun.exit_code}</span>}
            </div>

            <pre
              ref={outputRef}
              onScroll={handleOutputScroll}
              className="wc-mono"
              style={{
                background: 'var(--wc-bg)', border: '1px solid var(--wc-divider)', color: '#a7f3d0',
                padding: 16, borderRadius: 'var(--wc-radius-md)', fontSize: 12,
                overflowX: 'auto', overflowY: 'auto', maxHeight: '45vh', margin: 0,
              }}
            >
              {viewingRun.output || 'Noch keine Ausgabe...'}
            </pre>

            {viewingRun.output_truncated && (
              <p className="wc-muted" style={{ fontSize: 12, margin: 0 }}>⚠ Ausgabe wurde gekappt (Limit erreicht)</p>
            )}

            {viewingRun.after_state?.pending && viewingRun.after_state.pending.length > 0 && (
              <p className="wc-muted" style={{ fontSize: 12, margin: 0 }}>
                ⚠ Nach diesem Lauf sind noch {viewingRun.after_state.pending.length} Migration(en) offen (Teilerfolg oder Fehler).
              </p>
            )}

            <div style={{ display: 'flex', gap: 12 }}>
              {viewingRun.status === 'running' && (
                <button onClick={handleCancelRun} className="wc-btn wc-btn-secondary" style={{ flex: 1 }}>Abbrechen</button>
              )}
              <button onClick={handleCloseRunModal} className="wc-btn wc-btn-primary" style={{ flex: 1 }}>Schließen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
